import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertRailWallFixingBody, RailWallFixing, UpdateRailWallFixingBody } from "./types/railWallFixings";

export const useRailWallFixingsApi = () => {
  const api = useApi();

  const getAdminRailWallFixing = useCallback(
    (railWallFixingId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/rail_wall_fixings/${railWallFixingId}`;
      return useSWRMutation<RailWallFixing, CustomError, string>(url, (url) =>
        api.get<RailWallFixing>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminRailWallFixings = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rail_wall_fixings`, query);
      return useSWRImmutable<Paginated<RailWallFixing>, CustomError, string>(url, (url) =>
        api.get<Paginated<RailWallFixing>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminRailWallFixings = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rail_wall_fixings`, query);
      return api.get<Paginated<RailWallFixing>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminRailWallFixing = useCallback(
    async (body: InsertRailWallFixingBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/rail_wall_fixings`;
      const id = await api.post<{ id: string }, InsertRailWallFixingBody>(url, body);
      mutateCache([url, "/admin/settings/border/rails"]);
      return id;
    },
    [api],
  );

  const updateAdminRailWallFixing = useCallback(
    async (railWallFixingId: string, body: UpdateRailWallFixingBody): Promise<void> => {
      const url = `/admin/settings/border/rail_wall_fixings/${railWallFixingId}`;
      await api.put<UpdateRailWallFixingBody>(url, body);
      mutateCache(["/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rails"]);
    },
    [api],
  );

  const updateAdminRailWallFixingVisibility = useCallback(
    async (railWallFixingId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rail_wall_fixings/${railWallFixingId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rails"]));
    },
    [api],
  );

  const updateAdminRailWallFixingOrder = useCallback(
    async (railWallFixingId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rail_wall_fixings/${railWallFixingId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/rail_wall_fixings", "/admin/settings/border/rails"]));
    },
    [api],
  );

  const deleteAdminRailWallFixing = useCallback(
    async (railWallFixing: RailWallFixing): Promise<void> => {
      const url = `/admin/settings/border/rail_wall_fixings/${railWallFixing.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/rail_wall_fixings`, "/admin/settings/border/rails"]);
    },
    [api],
  );

  return {
    getAdminRailWallFixing,
    insertAdminRailWallFixing,
    updateAdminRailWallFixing,
    updateAdminRailWallFixingVisibility,
    updateAdminRailWallFixingOrder,
    deleteAdminRailWallFixing,
    useAdminRailWallFixings,
    getAdminRailWallFixings,
  };
};
